import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import DialogBox from '../Common/Layout/DialogBox';
import InputComponent from '../Common/Fields/InputComponent';
import { Backup } from '@mui/icons-material';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import * as XLSX from 'xlsx';
import {
  ICventContact,
  ICventEvent,
  ICventFileInput,
  ICventLocation,
  ICventRole,
  useGetAllCventRolesQuery,
  useUploadCventContactsMutation
} from '../../generated/graphql';
import { Actions } from '../../enums/ActionEnums';
import { AppContext } from '../../AppContext';

type Props = {
  handleClose: () => void;
  updateEvents: (event: ICventEvent) => void;
  updateLocations: (location: ICventLocation) => void;
};

export type IErrors = {
  location: string | null;
  fileName: string | null;
};

export const defaultErrorsObj: IErrors = {
  location: null,
  fileName: null
};

export const ErrorTexts = {
  location: 'Location is required',
  fileName: 'File is required'
};

export default function CventUpload({ handleClose, updateEvents, updateLocations }: Props) {
  const { dispatch } = useContext(AppContext);
  const [uploadContacts] = useUploadCventContactsMutation();
  const [fileName, setFileName] = useState('');
  const [fileContacts, setFileContacts] = useState<ICventContact[] | []>([]);
  const fileHeaders = 'event_name, first_name, last_name, company_name, title, email, role';
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [location, setLocation] = useState('');
  const allRoles = useGetAllCventRolesQuery();
  const [roles, setRoles] = useState<ICventRole[]>([]);

  useEffect(() => {
    if (allRoles.data && allRoles.data.result) {
      setRoles(allRoles.data?.result as ICventRole[]);
    }
  }, [allRoles.data]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        location: ErrorTexts.location
      });
    } else {
      setFormErrors({ ...formErrors, location: null });
    }
    setLocation(value);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target && event.target.files) {
      const { value } = event.currentTarget;
      if (!value) {
        setFormErrors({
          ...formErrors,
          fileName: ErrorTexts.fileName
        });
      } else {
        setFormErrors({ ...formErrors, fileName: null });
      }
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e && e.target && e.target.result) {
            const data = new Uint8Array(e.target.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
            setFileName(file.name);
            if (rows.length > 1) {
              const headers = fileHeaders.split(',').map((column) => column.trim());
              const data = rows.slice(1).map((row) => {
                const rowArr = row as (string | number | undefined)[];
                return headers.reduce((obj, key, i) => {
                  if (key === 'role') {
                    const roleName = rowArr[i]?.toString().trim().toLowerCase();
                    const matchedRole = roles.find(
                      (r) =>
                        r.name_short &&
                        r.name_short.trim().toLowerCase() === roleName?.toLowerCase()
                    );
                    if (!matchedRole && roleName) {
                      dispatch({
                        type: Actions.ShowMessage,
                        payload: {
                          severity: 'warning',
                          text: `Role "${roleName}" not found. This contact will not have a role assigned.`
                        }
                      });
                    }
                    return { ...obj, role_id: matchedRole ? Number(matchedRole.id) : null };
                  }
                  return { ...obj, [key]: rowArr[i] };
                }, {});
              });
              const raw = data as ICventContact[];
              setFileContacts(raw);
            } else {
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  severity: 'info',
                  text: 'No contacts to upload'
                }
              });
            }
          }
        };
        reader.readAsArrayBuffer(file);
      }
    }
    event.target.value = '';
    setFileName('');
  };

  const importContacts = () => {
    const errors = {
      location: null,
      fileName: null
    } as IErrors;
    let hasErrors = false;
    if (!location || location.length < 2) {
      hasErrors = true;
      errors.location = ErrorTexts.location;
    }
    if (!fileName) {
      hasErrors = true;
      errors.fileName = ErrorTexts.fileName;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      uploadContacts({
        variables: {
          location: location,
          input: fileContacts as ICventFileInput[]
        },
        update(_, { data }) {
          if (data?.result) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `Successfully imported file`
              }
            });
            updateEvents(data?.result.event as ICventEvent);
            updateLocations(data?.result.location as ICventLocation);
            handleClose();
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="event-upload-form-dialog"
      width={380}
      title="Import event file"
      onClose={handleClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', m: '30px', gap: '10px' }}>
        <InputComponent
          id="new-location-name"
          label="Location"
          value={location}
          onChange={handleNameChange}
          errorMsg={formErrors.location}
          additionalStyle={{ marginBottom: '5%' }}
        />
        <InputComponent
          id="event-upload-file"
          label=""
          value={fileName}
          onChange={handleFileUpload}
          errorMsg={formErrors.fileName}
          disabled
          endAdornment={
            <Box sx={{ position: 'relative', bottom: '3px' }}>
              <input
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                id="event-file"
                type="file"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                multiple={true}
              />
              <label htmlFor="event-file">
                <Backup
                  style={{
                    cursor: 'pointer',
                    paddingRight: '5%',
                    paddingTop: '5px',
                    color: Colors.main
                  }}
                />
              </label>
            </Box>
          }
        />
        <Box className="centralize" sx={{ gridColumn: 'span 3', marginBottom: '15px', mt: '20px' }}>
          <CancelButton id="cancel-event-upload-button" onClick={handleClose} text="Cancel" />
          <SaveButton id="add-new-event-button" onClick={() => importContacts()} text="Import" />
        </Box>
      </Box>
    </DialogBox>
  );
}
